import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
} from '@mui/material';
import {
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  CloudSync as CloudSyncIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import { useQuery } from '@apollo/client';
import { GET_BANK_ACCOUNTS_BY_COMPANY, GET_DOCUMENTS } from '../graphql/queries';
import { useCompany } from '../context/CompanyContext';
import { useAuth } from '../context/AuthContext';

const BankTransactions = () => {
  const { activeCompanyId } = useCompany();
  const { user, token, logout } = useAuth();
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [filterType, setFilterType] = useState('ALL'); // ALL, PENDING, LINKED

  // Date Filter State for transactions
  const [filterFromDate, setFilterFromDate] = useState('');
  const [filterToDate, setFilterToDate] = useState('');

  // Sync State (for Wise)
  const [syncLoading, setSyncLoading] = useState(false);
  const [fromDate, setFromDate] = useState(new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);

  // Linking Modal State
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch Bank Accounts
  const { data: accountsData } = useQuery(GET_BANK_ACCOUNTS_BY_COMPANY, {
    variables: { companyId: activeCompanyId },
  });

  // Auto-select first account when data loads
  useEffect(() => {
    if (accountsData?.bankAccountsByCompany?.length > 0 && !selectedAccountId) {
      setSelectedAccountId(accountsData.bankAccountsByCompany[0].id);
    }
  }, [accountsData, selectedAccountId]);

  // Fetch Documents for Linking
  const { data: documentsData } = useQuery(GET_DOCUMENTS, {
    variables: { companyId: activeCompanyId },
  });

  const fetchTransactions = async (fromDateOverride, toDateOverride) => {
    if (!selectedAccountId || !token) return;
    const fromDt = fromDateOverride !== undefined ? fromDateOverride : filterFromDate;
    const toDt = toDateOverride !== undefined ? toDateOverride : filterToDate;
    try {
      let url = `/api/bank/transactions?bankAccountId=${selectedAccountId}`;
      if (fromDt && toDt) {
        url += `&fromDate=${fromDt}&toDate=${toDt}`;
      }
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTransactions(data);
      } else if (response.status === 401) {
        console.error('Unauthorized request to bank API');
        alert("Сесията ви е изтекла. Моля, влезте отново, за да видите банковите транзакции.");
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (selectedAccountId && token) {
      fetchTransactions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountId, token]);

  // Get selected account object
  const selectedAccount = accountsData?.bankAccountsByCompany?.find(acc => acc.id === selectedAccountId);

  const handleSyncWise = async () => {
    if (!selectedAccount?.isWiseConnected || !token) return;
    setSyncLoading(true);
    try {
      const response = await fetch(`/api/bank/wise/sync?companyId=${activeCompanyId}&bankAccountId=${selectedAccountId}&fromDate=${fromDate}&toDate=${toDate}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const count = await response.json();
        alert(`Синхронизирани ${count} транзакции`);
        fetchTransactions();
      } else {
        const err = await response.text();
        alert(`Грешка: ${err}`);
      }
    } catch (e) {
      alert(`Грешка: ${e.message}`);
    } finally {
      setSyncLoading(false);
    }
  };

  const handleOpenLinkModal = (transaction) => {
    setSelectedTransaction(transaction);
    setSearchTerm(transaction.contraName || ''); // Auto-fill search with payer name
    setLinkModalOpen(true);
  };

  const handleLink = async (documentId) => {
    if (!token) return;
    try {
      const response = await fetch(`/api/bank/transactions/${selectedTransaction.id}/link?documentId=${documentId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setLinkModalOpen(false);
        fetchTransactions(); // Refresh
      } else {
        alert('Failed to link');
      }
    } catch (e) {
      alert(e.message);
    }
  };

  const handleUnlink = async (transactionId) => {
    if (!token) return;
    if (!window.confirm('Сигурни ли сте, че искате да премахнете връзката?')) return;
    try {
      const response = await fetch(`/api/bank/transactions/${transactionId}/unlink`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchTransactions(); // Refresh
      } else {
        alert('Failed to unlink');
      }
    } catch (e) {
      alert(e.message);
    }
  };

  const handleDelete = async (transactionId) => {
    if (!token) return;
    if (!window.confirm('Сигурни ли сте, че искате да изтриете тази транзакция? Това действие е необратимо.')) return;
    try {
      const response = await fetch(`/api/bank/transactions/${transactionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchTransactions(); // Refresh
        alert('Транзакцията е изтрита успешно.');
      } else {
        const err = await response.text();
        alert(`Грешка при изтриване: ${err}`);
      }
    } catch (e) {
      alert(e.message);
    }
  };

  const filteredTransactions = transactions.filter(tx => {
    if (filterType === 'PENDING') return tx.status === 'PENDING';
    if (filterType === 'LINKED') return tx.status === 'LINKED';
    return true;
  });

  // Filter documents for modal
  const unpaidDocuments = documentsData?.documentsByCompany?.filter(doc =>
    doc && doc.status !== 'PAID' && doc.status !== 'CANCELLED'
  ) || [];

  const filteredDocuments = unpaidDocuments.filter(doc =>
    doc && doc.documentNumber?.includes(searchTerm) ||
    doc?.client?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc?.totalAmountWithVat?.toString().includes(searchTerm)
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Обработка на плащания</Typography>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Банкова сметка</InputLabel>
          <Select
            value={selectedAccountId}
            label="Банкова сметка"
            onChange={(e) => setSelectedAccountId(e.target.value)}
          >
            {accountsData?.bankAccountsByCompany?.map(acc => (
              <MenuItem key={acc.id} value={acc.id}>
                {acc.bankName} ({acc.currencyCode})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {isAdmin && selectedAccount?.isWiseConnected && (
        <Paper sx={{ mb: 2, p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Wise Синхронизация</Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              label="От дата"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              label="До дата"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <Button
              variant="contained"
              color="info"
              startIcon={syncLoading ? <CircularProgress size={20} color="inherit" /> : <CloudSyncIcon />}
              onClick={handleSyncWise}
              disabled={syncLoading}
            >
              {syncLoading ? 'Синхронизиране...' : 'Синхронизирай от Wise'}
            </Button>
          </Box>
        </Paper>
      )}

      <Paper sx={{ mb: 2, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="От дата"
            type="date"
            value={filterFromDate}
            onChange={(e) => setFilterFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ width: 160 }}
          />
          <TextField
            label="До дата"
            type="date"
            value={filterToDate}
            onChange={(e) => setFilterToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ width: 160 }}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={fetchTransactions}
            startIcon={<SearchIcon />}
          >
            Филтрирай
          </Button>
          {(filterFromDate || filterToDate) && (
            <Button
              variant="text"
              size="small"
              onClick={() => {
                setFilterFromDate('');
                setFilterToDate('');
                fetchTransactions('', '');
              }}
            >
              Изчисти
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Chip
            label="Всички"
            onClick={() => setFilterType('ALL')}
            color={filterType === 'ALL' ? 'primary' : 'default'}
          />
          <Chip
            label="За обработка"
            onClick={() => setFilterType('PENDING')}
            color={filterType === 'PENDING' ? 'warning' : 'default'}
          />
          <Chip
            label="Обработени"
            onClick={() => setFilterType('LINKED')}
            color={filterType === 'LINKED' ? 'success' : 'default'}
          />
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Дата</TableCell>
              <TableCell>Сума</TableCell>
              <TableCell>Контрагент / Описание</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Свързан документ</TableCell>
              {isAdmin && <TableCell align="center">Действия</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTransactions.map((tx) => (
              <TableRow key={tx.id} hover>
                <TableCell>{tx.date}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'green' }}>{tx.amount}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">{tx.contraName}</Typography>
                  <Typography variant="caption" color="text.secondary">{tx.description}</Typography>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={tx.status === 'LINKED' ? 'Свързан' : 'Чакащ'} 
                    color={tx.status === 'LINKED' ? 'success' : 'warning'} 
                    size="small" 
                  />
                </TableCell>
                <TableCell>
                  {tx.document ? (
                     <Chip label={`Фактура #${tx.document.documentNumber}`} variant="outlined" />
                  ) : '-'}
                </TableCell>
                {isAdmin && (
                <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      {tx.status !== 'LINKED' ? (
                        <Tooltip title="Свържи с фактура">
                          <IconButton color="primary" onClick={() => handleOpenLinkModal(tx)}>
                            <LinkIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Премахни връзката">
                          <IconButton color="error" onClick={() => handleUnlink(tx.id)}>
                            <LinkOffIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Изтрий транзакцията">
                        <IconButton color="error" onClick={() => handleDelete(tx.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                </TableCell>
                )}
              </TableRow>
            ))}
            {filteredTransactions.length === 0 && (
              <TableRow>
                <TableCell colSpan={isAdmin ? 6 : 5} align="center">Няма намерени транзакции</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Link Modal */}
      <Dialog open={linkModalOpen} onClose={() => setLinkModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Свързване на плащане</DialogTitle>
        <DialogContent>
          {selectedTransaction && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Изберете фактура за покриване на сума: <strong>{selectedTransaction.amount}</strong> от <strong>{selectedTransaction.contraName}</strong>
            </Alert>
          )}
          
          <TextField 
            fullWidth 
            placeholder="Търсене по номер или клиент..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
            }}
            sx={{ mb: 2 }}
          />

          <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Номер</TableCell>
                  <TableCell>Клиент</TableCell>
                  <TableCell>Дата</TableCell>
                  <TableCell>Сума</TableCell>
                  <TableCell>Действие</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDocuments.map(doc => (
                  <TableRow key={doc.id}>
                    <TableCell>{doc.documentNumber}</TableCell>
                    <TableCell>{doc.client.name}</TableCell>
                    <TableCell>{doc.issueDate}</TableCell>
                    <TableCell>{doc.totalAmountWithVat} {doc.currencyCode}</TableCell>
                    <TableCell>
                      <Button 
                        size="small" 
                        variant="contained" 
                        onClick={() => handleLink(doc.id)}
                        startIcon={<CheckCircleIcon />}
                      >
                        Избери
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkModalOpen(false)}>Отказ</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BankTransactions;